"use client";

import type React from "react";
import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CreateUserCustomerIdFormSchema } from "./schema";
import type { z } from "zod";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import type { User } from "@/actions/types";
import hooks from "@/hooks";
import { usePathname, useRouter } from "next/navigation";
import useCreateUserAddress from "@/hooks/use-create-user-address.hook";
import { toast } from "@/hooks/use-toast";

type CustomerID = z.infer<typeof CreateUserCustomerIdFormSchema>;

interface ICreateUserCustomerIdForm {
  user: User;
}

const CreateUserCustomerIdForm = ({ user }: ICreateUserCustomerIdForm) => {
  const [cepError, setCepError] = useState<string | null>(null);

  const form = useForm<CustomerID>({
    resolver: zodResolver(CreateUserCustomerIdFormSchema),
    defaultValues: {
      address: "",
      addressNumber: "",
      complement: "",
      cpfCnpj: "",
      province: "",
      postalCode: "",
      neighborhood: "",
      city: "",
      country: "Brasil",
    },
  });

  const Router = useRouter();
  const Pathname = usePathname();

  const userCustomerIdService = hooks.useCreateUserCustomerId({
    onSuccess: () => {},
    onError: (error) => {
      toast({
        title: "Algo deu errado...",
        description: error.message,
        variant: "warning",
      });
    },
  });

  const userAddressService = useCreateUserAddress({
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error creating address:", error);
    },
  });

  const fetchAddressByCep = async (cep: string) => {
    if (!cep || cep.length < 8) return;

    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length !== 8) return;

    setCepError(null);

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      );
      const data = await response.json();

      if (data.erro) {
        setCepError("CEP não encontrado");
        return;
      }

      form.setValue("address", data.logradouro);
      form.setValue("neighborhood", data.bairro);
      form.setValue("city", data.localidade);
      form.setValue("province", data.estado);

      const numberField = document.querySelector(
        'input[name="addressNumber"]',
      ) as HTMLInputElement;
      if (numberField) {
        numberField.focus();
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      setCepError("Erro ao buscar CEP. Tente novamente.");
    }
  };

  const handleCepBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cep = e.target.value;
    fetchAddressByCep(cep);
  };

  async function onSubmit(data: CustomerID) {
    try {
      await userCustomerIdService.mutateAsync({
        user_id: user.id,
        cpfCnpj: data.cpfCnpj,
        name: user.name,
        address: `${data.address}, ${data.addressNumber} - ${data.complement} - ${data.neighborhood} - ${data.city}`,
        postalCode: data.postalCode,
        province: data.province,
        complement: data.complement,
        email: user.email,
        mobilePhone: user.phone,
      });

      await userAddressService.mutateAsync({
        userId: user.id,
        address: {
          street: data.address,
          number: data.addressNumber,
          district: data.neighborhood,
          city: data.city,
          state: data.province,
          country: data.country,
          zip_code: data.postalCode,
          complement: data.complement,
        },
      });

      localStorage.setItem("cpfCnpj", data.cpfCnpj);

      Router.push(`${Pathname}/salon`);
    } catch (error) {
      console.error("Error during form submission:", error);
    }
  }

  return (
    <div className="w-full bg-white p-4 sm:p-6 md:p-8 rounded-3xl shadow-lg flex flex-col">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="p-4 sm:p-6 md:p-8 w-full flex flex-col items-center gap-y-4 sm:gap-y-6"
        >
          <div className="w-full grid grid-cols-1 gap-4">
            <FormFields.InputFormField
              form={form}
              formName="cpfCnpj"
              label="CPF/CNPJ"
              placeholder="___.___.___-__"
              mask="document"
            />
            <div className="relative">
              <FormFields.InputFormField
                form={form}
                formName="postalCode"
                label="CEP"
                placeholder="_____-___"
                mask="cep"
                onBlurFn={handleCepBlur}
              />
              {cepError && (
                <p className="text-sm text-red-500 mt-1">{cepError}</p>
              )}
            </div>
          </div>
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <FormFields.InputFormField
              form={form}
              formName="address"
              label="Endereço"
              disabled={true}
              noClearButton={true}
            />
            <FormFields.InputFormField
              form={form}
              formName="addressNumber"
              label="Número"
            />
            <FormFields.InputFormField
              form={form}
              formName="complement"
              label="Complemento"
            />
          </div>
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormFields.InputFormField
              form={form}
              formName="neighborhood"
              label="Bairro"
              disabled={true}
              noClearButton={true}
            />
            <FormFields.InputFormField
              form={form}
              formName="city"
              label="Cidade"
              disabled={true}
              noClearButton={true}
            />
          </div>
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormFields.InputFormField
              form={form}
              formName="province"
              label="Estado"
              disabled={true}
              noClearButton={true}
            />
            <FormFields.InputFormField
              form={form}
              formName="country"
              label="País"
              placeholder="Brasil"
            />
          </div>
          <button type="submit" id="hiddenSubmitButton" className="hidden" />
        </form>
      </Form>
    </div>
  );
};

export default CreateUserCustomerIdForm;
